import { cn } from "@/lib/utils";

// UI-2: the redesigned Technical Audit overview. The signature element is the
// severity spectrum — a single proportional bar that IS the primary filter
// affordance (click a band to triage that severity). Replaces the generic
// stat-cards + donut. Presentational: the page wires data + navigation.

export type Severity = "critical" | "warning" | "info";

export interface FixNextItem {
	issueType: string;
	label: string;
	count: number;
	effort?: "Easy" | "Medium" | "Hard";
}

export interface AuditOverviewProps {
	website: string;
	/** Health score 0–100. */
	health: number;
	/** Optional previous score to show a trend. */
	previousHealth?: number | null;
	counts: { critical: number; warning: number; info: number };
	fixNext: FixNextItem[];
	onSelectSeverity: (severity: Severity) => void;
	onSelectType: (issueType: string) => void;
}

const SEVERITY_META: Record<
	Severity,
	{ label: string; bar: string; dot: string; text: string }
> = {
	// Theme tokens only (no raw palette): destructive = critical, coral = warning, teal = info.
	critical: { label: "critical", bar: "bg-destructive", dot: "bg-destructive", text: "text-destructive" },
	warning: { label: "warning", bar: "bg-coral", dot: "bg-coral", text: "text-coral-dark" },
	info: { label: "info", bar: "bg-teal", dot: "bg-teal", text: "text-teal-dark" },
};

const EFFORT_TEXT: Record<string, string> = {
	Easy: "text-teal-dark",
	Medium: "text-coral-dark",
	Hard: "text-destructive",
};

export function AuditOverview({
	website,
	health,
	previousHealth,
	counts,
	fixNext,
	onSelectSeverity,
	onSelectType,
}: AuditOverviewProps) {
	const total = counts.critical + counts.warning + counts.info;
	const order: Severity[] = ["critical", "warning", "info"];
	const trend =
		previousHealth == null ? null : health - previousHealth;

	return (
		<section className="space-y-6">
			{/* Header: site + health with trend */}
			<div className="flex items-end justify-between gap-4">
				<div>
					<h2 className="text-xl font-semibold text-foreground">{website}</h2>
					<p className="text-sm text-muted-foreground">
						{total} {total === 1 ? "issue" : "issues"} found across this audit
					</p>
				</div>
				<div className="text-right">
					<div className="flex items-baseline gap-1.5 justify-end">
						<span className="text-3xl font-semibold tabular-nums text-foreground">{health}</span>
						<span className="text-sm text-muted-foreground">/100 health</span>
					</div>
					{trend != null && (
						<div
							className={cn(
								"text-xs font-medium",
								trend > 0 && "text-teal-dark",
								trend < 0 && "text-destructive",
								trend === 0 && "text-muted-foreground",
							)}
						>
							{trend > 0 ? "▲" : trend < 0 ? "▼" : "—"} {Math.abs(trend)} vs last audit
						</div>
					)}
				</div>
			</div>

			{/* Signature: clickable severity spectrum */}
			<div>
				<div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="group" aria-label="Severity spectrum">
					{order.map((sev) => {
						const value = counts[sev];
						if (value === 0) return null;
						const pct = total > 0 ? (value / total) * 100 : 0;
						return (
							<button
								key={sev}
								type="button"
								onClick={() => onSelectSeverity(sev)}
								aria-label={`Filter ${value} ${SEVERITY_META[sev].label} issues`}
								title={`${value} ${SEVERITY_META[sev].label} — click to filter`}
								className={cn(
									"h-full transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400",
									SEVERITY_META[sev].bar,
								)}
								style={{ width: `${pct}%` }}
							/>
						);
					})}
				</div>
				{/* Legend doubles as clickable counts */}
				<div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
					{order.map((sev) => (
						<button
							key={sev}
							type="button"
							onClick={() => onSelectSeverity(sev)}
							className="group flex items-center gap-2 text-sm focus:outline-none"
						>
							<span className={cn("h-2.5 w-2.5 rounded-full", SEVERITY_META[sev].dot)} />
							<span className="tabular-nums font-semibold text-foreground">{counts[sev]}</span>
							<span className="text-muted-foreground capitalize group-hover:text-foreground">
								{SEVERITY_META[sev].label}
							</span>
						</button>
					))}
				</div>
			</div>

			{/* Fix next: ranked, actionable */}
			<div>
				<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
					Fix next
				</div>
				{fixNext.length === 0 ? (
					<p className="text-sm text-muted-foreground">Nothing outstanding — nice.</p>
				) : (
					<ul className="grid gap-2 sm:grid-cols-2">
						{fixNext.map((item) => (
							<li key={item.issueType} className="rounded-lg border border-border bg-card">
								<button
									type="button"
									onClick={() => onSelectType(item.issueType)}
									className="flex w-full items-center justify-between gap-3 rounded-lg px-4 py-3 text-left hover:bg-muted focus:outline-none focus:bg-muted"
								>
									<span className="flex items-center gap-3">
										<span className="text-sm font-medium text-foreground">{item.label}</span>
										<span className="tabular-nums text-xs text-muted-foreground">
											{item.count} {item.count === 1 ? "page" : "pages"}
										</span>
										{item.effort && (
											<span className={cn("text-xs font-medium", EFFORT_TEXT[item.effort])}>
												{item.effort}
											</span>
										)}
									</span>
									<span className="text-sm font-medium text-coral">Fix →</span>
								</button>
							</li>
						))}
					</ul>
				)}
			</div>
		</section>
	);
}
